import { ButtonBase, SvgIcon, SxProps } from '@mui/material';
import copy from 'copy-to-clipboard';
import * as queryString from 'query-string';
import React, { ReactNode } from 'react';

import FilterIcon from '@sbu/assets.svg/src/filter.svg';
import SearchIcon from '@sbu/assets.svg/src/search.svg';
import ActionIcon from '@sbu/assets.svg/src/submit.svg';
import { Link } from '@sbu/components.link';
import { useTheme } from '@sumologic/yama-ds-components';

import { useAppInfoQuery } from 'app/AppInfoQuery';
import { List, ListHeader, ListItem } from 'shared/components/layout/List';

import { ContextActionProps } from './ContextAction';
import css from './ContextAction.module.scss';
import { ContextActionMenuItem } from './ContextActionMenuItem';
import { ContextActionRecordSearchItem } from './ContextActionRecordSearchItem';
import { Match } from './utils';

interface ContextActionMenuItemsProps
  extends Pick<
    ContextActionProps,
    | 'contextData'
    | 'eventTimestamp'
    | 'recordField'
    | 'sumoLogicRegion'
    | 'value'
  > {
  children?: ReactNode;
  matches: Match[];
  matchedContextActions: ContextActionProps['contextActions'];
  onQuickFilter?: (entityValue: string, negate?: boolean) => void;
}

export const ContextActionMenuItems = ({
  children,
  contextData,
  eventTimestamp,
  matches,
  matchedContextActions,
  recordField,
  sumoLogicRegion,
  value,
  onQuickFilter,
}: ContextActionMenuItemsProps) => {
  const { data } = useAppInfoQuery();
  const isFederatedCluster = data?.isFederatedCluster ?? false;
  const { palette } = useTheme();

  const canCopy = ['number', 'string'].includes(typeof children);

  const [match] = matches;
  const contentValue = match?.content;
  const type = match?.type;

  const hasSearchSection = !!matches.length && !isFederatedCluster;
  const hasQuickFilters = !!onQuickFilter;
  const hasActionsSection =
    type === 'url' || !isFederatedCluster || !!matchedContextActions.length;

  const cleanContentUrl = (content?: string) => {
    return content?.replace(/\\/g, '') ?? content;
  };

  const keywordSearchUrl = `/sec/search?${queryString.stringify({
    q: contentValue,
  })}`;

  const onClickContextAction: React.MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  const handleCopyExpression: React.MouseEventHandler = (e) => {
    onClickContextAction(e);

    if (!recordField || !contextData) {
      return copy(String(children));
    }

    if (recordField.startsWith('fields.')) {
      const fieldName = `fields["${recordField.substring(
        recordField?.indexOf('.') + 1
      )}"]`;

      const field = contextData.fields[recordField.replace(/^fields\./, '')];

      copy(
        `${fieldName} = ${typeof field === 'string' ? `"${field}"` : field}`
      );
    } else {
      const field = contextData[recordField];
      copy(
        `${recordField} = ${typeof field === 'string' ? `"${field}"` : field}`
      );
    }
  };

  const handleCopyValue: React.MouseEventHandler = (e) => {
    onClickContextAction(e);
    // `canCopy` above checks whether this is a string or number anyway
    copy(children as string);
  };

  const buttonLinkStyles: SxProps = {
    color: palette.tokens.YamaLinkPrimaryDefaultColor,
    textAlign: 'left',
    transition: 'color 0.3s',
    '&:hover': {
      color: palette.tokens.YamaLinkPrimaryHoverColor,
    },
  };

  return (
    <List className={css.menuItems} data-sumo-test='context-action-menu-items'>
      {hasSearchSection && (
        <>
          <ListHeader className={css.listHeader}>
            <SearchIcon className={css.searchIcon} />
            Search
          </ListHeader>
          {!isFederatedCluster && (
            <>
              <ListItem
                className={css.listItem}
                data-sumo-test='context-action-search'
              >
                <Link
                  href={keywordSearchUrl}
                  rel='noopener noreferrer'
                  target='_blank'
                  onClick={onClickContextAction}
                >
                  Keyword Search Insights, Signals and Entities for{' '}
                  {contentValue}
                </Link>
              </ListItem>
              <ContextActionRecordSearchItem
                contentValue={contentValue}
                contextData={contextData}
                eventTimestamp={eventTimestamp}
                value={value}
                onClick={onClickContextAction}
              />
            </>
          )}
        </>
      )}
      {hasSearchSection && hasQuickFilters && (
        <hr className={css.listDivider} />
      )}
      {hasQuickFilters && (
        <>
          <ListHeader className={css.listHeader}>
            <SvgIcon
              component={FilterIcon}
              sx={{
                height: 14,
                width: 14,
                flexShrink: 0,
                marginRight: 1,
                color: ({ palette }) => palette.tokens.YamaPaletteGrey500Color,
              }}
            />
            Quick Filter
          </ListHeader>
          <ListItem className={css.listItem}>
            <ButtonBase
              onClick={(e) => {
                onClickContextAction(e);
                onQuickFilter?.(contentValue, true);
              }}
              sx={buttonLinkStyles}
            >
              Exclude {contentValue} from results
            </ButtonBase>
          </ListItem>
          <ListItem className={css.listItem}>
            <ButtonBase
              onClick={(e) => {
                onClickContextAction(e);
                onQuickFilter?.(contentValue);
              }}
              sx={buttonLinkStyles}
            >
              Only show {contentValue} in results
            </ButtonBase>
          </ListItem>
        </>
      )}
      {(hasSearchSection || hasQuickFilters) && hasActionsSection && (
        <hr className={css.listDivider} />
      )}
      {hasActionsSection && (
        <>
          <ListHeader className={css.listHeader}>
            <ActionIcon className={css.headerIcon} />
            Actions
          </ListHeader>
          {type === 'url' && (
            <ListItem
              className={css.listItem}
              data-sumo-test='context-action-open-link'
            >
              <Link
                href={cleanContentUrl(contentValue) ?? ''}
                rel='noopener noreferrer'
                target='_blank'
                variant='a'
                onClick={onClickContextAction}
              >
                Open URL (Unsafe)
              </Link>
            </ListItem>
          )}
          {canCopy && (
            <>
              <ListItem className={css.listItem} key='copy-value'>
                <ButtonBase
                  data-sumo-test='context-action-action'
                  onClick={handleCopyValue}
                  sx={buttonLinkStyles}
                >
                  Copy value
                </ButtonBase>
              </ListItem>
              <ListItem className={css.listItem} key='copy-expression'>
                <ButtonBase
                  data-sumo-test='context-action-action'
                  onClick={handleCopyExpression}
                  sx={buttonLinkStyles}
                >
                  Copy expression
                </ButtonBase>
              </ListItem>
            </>
          )}
          {/** TODO: The following 2 actions are temporary to support add-to actions */}
          {!isFederatedCluster && (
            <>
              <ListItem className={css.listItem} key='add-to-match-list'>
                <Link
                  data-sumo-test='context-action-action'
                  href={`/sec/content/match-lists?value=${contentValue}`}
                  rel='noopener noreferrer'
                  target='_blank'
                  onClick={onClickContextAction}
                >
                  Add To Match List
                </Link>
              </ListItem>
              <ListItem className={css.listItem} key='add-to-suppressed-list'>
                <Link
                  data-sumo-test='context-action-action'
                  href={`/sec/content/suppressed-lists?value=${contentValue}`}
                  rel='noopener noreferrer'
                  target='_blank'
                  onClick={onClickContextAction}
                >
                  Add To Suppressed List
                </Link>
              </ListItem>
            </>
          )}
          {matchedContextActions.map((contextAction) => (
            <ContextActionMenuItem
              contextAction={contextAction}
              contextData={contextData}
              key={contextAction.id}
              sumoLogicRegion={sumoLogicRegion}
              value={value}
              onClick={onClickContextAction}
            >
              {children}
            </ContextActionMenuItem>
          ))}
        </>
      )}
    </List>
  );
};
